import React, { useState } from "react";
import { Modal, Row, Col, Button } from "react-bootstrap";
import Select, { components } from "react-select";
import * as Icon from "react-bootstrap-icons";
import { saveCustomField } from "./CustomFieldCreate.service";
import CustomButton from "../../../../../pages/reusable_components/custom_button_component/Button";
import { CustomFieldCreateProps } from "./CustomFieldCreate.utils";
import { isInBuiltCustomFieldCategory } from "../../../../../Reusables/utilities/customFieldCategory.utils";

/**
 * CustomFieldCreate Component
 *
 * A modal form for creating custom MDM fields for Client Data Management or Engagement Letters.
 * Supports different field types like text, checkbox, dropdown, etc., and conditionally renders
 * value option inputs for dropdown-type fields.
 */
const CustomFieldCreate: React.FC<CustomFieldCreateProps> = ({
  show,
  onHide,
  category,
  onFieldCreated,
}) => {
  const [fieldName, setFieldName] = useState("");
  const [selectedOptions, setSelectedOptions] = useState<
    { id: number; label: string; value: string }[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const isBlockedCategory = isInBuiltCustomFieldCategory(category);

  const typeOptions = [
    {
      value: "text",
      label: "Text",
      id: "text",
      logo: (
        <Icon.TextParagraph
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
    {
      value: "password",
      label: "Password",
      id: "password",
      logo: (
        <Icon.Key style={{ fontSize: 20, color: "var(--theme1_primary)" }} />
      ),
    },
    {
      value: "numberinput",
      label: "Number",
      id: "numberinput",
      logo: (
        <Icon.Icon123
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
    {
      value: "checkbox",
      label: "Checkbox",
      id: "checkbox",
      logo: (
        <Icon.Check2Square
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
    {
      value: "select",
      label: "Dropdown",
      id: "select",
      logo: (
        <Icon.MenuButtonWide
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
    {
      value: "date",
      label: "Date",
      id: "date",
      logo: (
        <Icon.Calendar2Date
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
    {
      value: "file",
      label: "File",
      id: "file",
      logo: (
        <Icon.FileEarmarkMedical
          style={{ fontSize: 20, color: "var(--theme1_primary)" }}
        />
      ),
    },
  ];
  const [selectedType, setSelectedType] = useState(typeOptions[0]);

  const { Option, Control } = components;

  const IconOption = (props: any) => (
    <Option {...props}>
      {props.data.logo} &nbsp;&nbsp;&nbsp;&nbsp;
      {props.data.label}
    </Option>
  );

  const IconControl = ({ children, ...props }: any) => (
    <Control {...props}>
      &nbsp;&nbsp;&nbsp;&nbsp;
      {props.selectProps.value.logo} &nbsp;&nbsp;
      {children}
    </Control>
  );

  /**
   * OptionSelector Component
   * Allows adding/removing/editing values for select-type custom fields.
   */
  const OptionSelector: React.FC<{
    setSelectedOptions: (
      options: { id: number; label: string; value: string }[]
    ) => void;
  }> = ({ setSelectedOptions }) => {
    const [options, setOptions] = useState([{ id: 1, label: "", value: "" }]);

    const addOption = () => {
      const newOption = { id: options.length + 1, label: "", value: "" };
      const updated = [...options, newOption];
      setOptions(updated);
      setSelectedOptions(updated);
    };

    const updateOption = (id: number, updatedValue: string) => {
      const updated = options.map((item) =>
        item.id === id
          ? { ...item, label: updatedValue, value: updatedValue }
          : item
      );
      setOptions(updated);
      setSelectedOptions(updated);
    };

    const deleteOption = (id: number) => {
      const updated = options.filter((item) => item.id !== id);
      setOptions(updated);
      setSelectedOptions(updated);
    };

    return (
      <>
        <div
          className="flex-row-center-center"
          style={{ marginBottom: 10, marginTop: 20 }}
        >
          <label style={{ fontWeight: 500, fontSize: 14 }}>
            Field Value Options
          </label>
          <Icon.PlusCircleFill
            color="var(--theme1_primary)"
            style={{ fontSize: 20, marginLeft: 20 }}
            onClick={addOption}
          />
        </div>
        {options.map((option) => (
          <div
            key={option.id}
            className="flex-row-center-center"
            style={{ margin: "5px 0px" }}
          >
            <input
              type="text"
              className="form-control move-left custom-input"
              placeholder="Enter Value"
              value={option.value}
              onChange={(e) => updateOption(option.id, e.target.value)}
              autoComplete="nope"
            />
            <Icon.DashCircleFill
              color="var(--theme1_primary)"
              style={{ fontSize: 20, marginLeft: 20 }}
              onClick={() => deleteOption(option.id)}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      centered
      scrollable={false}
      keyboard={false}
    >
      <Modal.Header>
        <h5>Add Custom Field</h5>
      </Modal.Header>
      <Modal.Body
        className="flex-column-start-center"
        style={{ padding: "0px 15px" }}
      >
        <Row className="no-gutter" style={{ marginBottom: 20, width: "100%" }}>
          <Col md={12} style={{ marginBottom: 20 }}>
            <label>Field Name</label>
            <input
              type="text"
              className="form-control move-left custom-input"
              placeholder="Enter Field Name"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              autoComplete="nope"
            />
          </Col>
          <Col md={12}>
            <label>Field Type</label>
            <Select
              value={selectedType}
              onChange={(e) => e && setSelectedType(e)}
              options={typeOptions}
              components={{ Control: IconControl, Option: IconOption }}
              className="form-select custom-select"
              menuPosition="fixed"
              classNamePrefix="react-select"
            />
          </Col>
          <Col md={12}>
            {selectedType.value === "select" && (
              <OptionSelector setSelectedOptions={setSelectedOptions} />
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="action-footer">
        <Row
          className="no-gutter"
          style={{ padding: 0, margin: 0, width: "100%" }}
        >
          <Col md={6} style={{ width: "fit-content" }}>
            <CustomButton
              disabled={isBlockedCategory}
              onclick={() => {
                if (isBlockedCategory) return;
                saveCustomField(
                  {
                    field_name: fieldName,
                    field_type: selectedType.value,
                    field_category: category,
                    field_purpose: [
                      "Client Data Management",
                      "Engagement Letter",
                    ],
                    field_options: selectedOptions,
                  },
                  setIsSaving,
                  onFieldCreated
                );
              }}
              content={"OK"}
              progress={isSaving}
            />
          </Col>
          <Col
            md={6}
            className="flex-row-space-center"
            style={{ width: "fit-content" }}
          >
            <Button onClick={onHide} className="custom-button">
              <p
                className="icon-wrapper"
                style={{ marginBottom: 0, marginTop: 0 }}
              >
                Cancel
              </p>
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomFieldCreate;
